import { DateTime, Duration, Interval } from 'luxon';
import { ChangeEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Path } from 'react-hook-form';
import type { DateFilter, DatePickerProps, DateRange, TimeFrameOption } from 'types/common';
import { dateTimeToInputDate, inputDateToDateTime } from 'utils/dates';
import { isValidDateFilter } from 'utils/filters';
import { type FilterProps } from '../FilterAccordion/FilterAccordion';
import { InputFilters } from '../shared';

/**
 * DO NOT MEMOIZE. Leverages now() for current date.
 * @param dateFilter the date filter to convert
 * @returns Date range
 */
const dateFilterToDateRange = (dateFilter: DateFilter): DateRange | undefined => {
  const { timeframe } = dateFilter;
  const now = DateTime.now();

  let startTime;
  let endTime;
  let duration: Duration;

  switch (timeframe) {
    case 'custom':
    case undefined:
      startTime = inputDateToDateTime(dateFilter.start);
      endTime = inputDateToDateTime(dateFilter.end);

      if (!startTime || !endTime) {
        return undefined;
      }
      return {
        startTime,
        endTime,
      };

    case 'thisMonth':
      return {
        startTime: now.startOf('month'),
        endTime: now.endOf('day'),
      };

    case 'nextMonth':
      return {
        startTime: now.endOf('day'),
        endTime: now.endOf('month'),
      };

    case 'thisYear':
      return {
        startTime: now.startOf('year'),
        endTime: now.endOf('day'),
      };

    default:
      // Everything else is a duration
      if (timeframe.startsWith('-')) {
        duration = Duration.fromISO(timeframe.slice(1));

        return {
          startTime: now.minus(duration),
          endTime: now.endOf('day'),
        };
      }

      duration = Duration.fromISO(timeframe!);

      return {
        startTime: now.endOf('day'),
        endTime: now.plus(duration),
      };
  }
};

const dateFilterToInputFilter = (dateFilter?: DateFilter) => {
  if (!isValidDateFilter(dateFilter)) {
    return undefined;
  }

  const inputDateFilter = dateFilterToDateRange(dateFilter);

  if (!inputDateFilter) {
    return undefined;
  }

  const newLastActiveDateFilter = {
    start: dateTimeToInputDate(inputDateFilter.startTime ?? undefined),
    end: dateTimeToInputDate(inputDateFilter.endTime ?? undefined),
  };

  if (newLastActiveDateFilter.start && newLastActiveDateFilter.end) {
    return newLastActiveDateFilter;
  }

  return undefined;
};

const invalidRangeMessage = 'Start date must be before end date.' as const;

interface DateFilterProps extends FilterProps {
  options: Array<TimeFrameOption>;
  filter?: DateFilter;
  filterPath: Path<InputFilters>;
  timeframePath: Path<InputFilters>;
  startPath: Path<InputFilters>;
  endPath: Path<InputFilters>;
  minDate?: DateTime<true>;
  maxDate?: DateTime<true> | null;
}

const useDateFilter = ({
  options,
  filter,
  submit,
  filterPath,
  timeframePath,
  startPath,
  endPath,
  form,
  minDate,
  maxDate,
}: DateFilterProps) => {
  const { register, setValue, getValues, trigger } = form;

  const [lastFocusedField, setLastFocusedField] = useState<Path<InputFilters>>(startPath);

  const selectableOptions = useMemo<Array<{ label: string; value: string }>>(
    () =>
      options.map(({ label, value = 'unknown', isFuture }) => {
        if (typeof value === 'string') {
          return {
            label,
            value: value ?? 'unknown',
          };
        }

        if (isFuture) {
          return { label, value: value.toISO() ?? 'unknown' };
        }

        return { label, value: `-${value!.toISO()}` };
      }),
    [options]
  );

  const lastTimeframeRef = useRef(filter?.timeframe);

  useEffect(() => {
    const timeframe = filter?.timeframe;
    if (lastTimeframeRef.current !== timeframe) {
      lastTimeframeRef.current = timeframe;

      if (timeframe && timeframe !== 'custom') {
        const newRange = dateFilterToInputFilter(filter);
        if (newRange) {
          setValue(filterPath, {
            timeframe,
            ...newRange,
          });

          submit();
        }
      }
    } else if (timeframe !== 'custom' && (!filter?.start || !filter?.end)) {
      const newRange = dateFilterToInputFilter(filter);
      if (newRange) {
        setValue(filterPath, {
          timeframe,
          ...newRange,
        });

        submit();
      }
    }
  }, [filter, filterPath, setValue, submit]);

  const handleDateChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (e) => {
      setValue(timeframePath, 'custom');

      const dateChangedPath: Path<InputFilters> = e.target.name as any;
      setLastFocusedField(dateChangedPath);

      if (!(await trigger(dateChangedPath))) {
        return;
      }

      submit();
    },
    [setValue, submit, timeframePath, trigger]
  );

  const handleTimeframeChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(() => {
    submit();
  }, [submit]);

  const timeframeProps = register(timeframePath, {
    required: true,
    onChange: handleTimeframeChange,
  });

  const start = getValues(startPath) as string | undefined;
  const end = getValues(endPath) as string | undefined;

  const { startDate, endDate } = useMemo(
    () => ({ startDate: inputDateToDateTime(start), endDate: inputDateToDateTime(end) }),
    [end, start]
  );

  const validateInterval = useCallback(() => {
    if (!(startDate && endDate)) {
      return false;
    }

    return Interval.fromDateTimes(startDate, endDate).isValid;
  }, [endDate, startDate]);

  const { invalid, validationMessage } = useMemo(() => {
    let newInvalid: boolean | undefined = false;

    if (!validateInterval()) {
      newInvalid = startDate && endDate ? true : undefined;
    }

    return {
      invalid: newInvalid,
      validationMessage: newInvalid ? invalidRangeMessage : undefined,
    };
  }, [endDate, startDate, validateInterval]);

  const startFieldProps: DatePickerProps = {
    ...register(startPath, {
      onChange: handleDateChange,
    }),
    invalid:
      (invalid && lastFocusedField === startPath) ||
      (start && start.length > 0 ? undefined : false),
    defaultValue: start,
    minDate,
    maxDate,
  };

  const endFieldProps: DatePickerProps = {
    ...register(endPath, {
      onChange: handleDateChange,
    }),
    invalid:
      (invalid && lastFocusedField === endPath) || (end && end.length > 0 ? undefined : false),
    defaultValue: end,
    minDate,
    maxDate,
  };

  return {
    selectableOptions,
    timeframeProps,
    startFieldProps,
    endFieldProps,
    invalid,
    validationMessage,
  };
};

export default useDateFilter;
